// ReportLostForm.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Renew.css";
import useEscapeToGoBack from "../hooks/useEscapeToGoBack";

const API_SEARCH = "https://www.lianfoong.com/api/pawn-transactions/search";
const API_REPORT = "https://www.lianfoong.com/api/pawn-transactions/report-lost";
const API_REPORT_AND_PRINT = "https://www.lianfoong.com/api/pawn-transactions/report-lost-with-letter";
const API_PRINT_ONLY       = "https://www.lianfoong.com/api/pawn-transactions/print-lost-form"; // if you keep it

export default function ReportLostForm() {
  useEscapeToGoBack();
  const navigate = useNavigate();

  const [mmyy, setMMYY] = useState("");
  const [receiptNo, setReceiptNo] = useState(""); // last digits only (1–4)
  const [transaction, setTx] = useState(null);
  const [error, setError] = useState("");
  const [confirmStep, setConfirmStep] = useState(0); // 0=initial, 1=final

  const mRef = useRef(null);
  const rRef = useRef(null);

  useEffect(() => {
    requestAnimationFrame(() => {
      mRef.current?.focus();
      mRef.current?.select?.();
    });
  }, []);

  // helpers
  const cleanMMYY = (v) => v.replace(/\D/g, "").slice(0, 4);
  const cleanDigits = (v) => v.replace(/\D/g, "").slice(0, 4);
  const pad4 = (v) => v.padStart(4, "0");
  const fmtDate = (s) => {
    const m = String(s || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
  };

  const handleSearch = async () => {
    const mm = cleanMMYY(mmyy);
    const last = cleanDigits(receiptNo);

    if (mm.length !== 4 || last.length === 0) {
      setError("Enter MMYY and the last receipt digits.");
      setTx(null);
      return;
    }

    try {
      setError("");
      const res = await axios.get(API_SEARCH, { params: { mmyy: mm, receiptNo: last } });
      setTx(res.data);
      setConfirmStep(0);
    } catch (e) {
      setTx(null);
      setError(e?.response?.data || "Transaction not found!");
    }
  };

  function openPdfBlob(blob, filename) {
  const url = URL.createObjectURL(blob); // blob is already a PDF
  const win = window.open(url, "_blank");
  if (!win) {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

const handleReportLost = async () => {
  if (!transaction) return;
  const mm = mmyy.replace(/\D/g, "").slice(0,4);
  const last = receiptNo.replace(/\D/g, "").slice(0,4).padStart(4,"0");
  const rec = `B/${mm}/${last}`;

  try {
    const res = await axios.put(API_REPORT_AND_PRINT, null, {
      params: { receiptNo: rec },
      responseType: "blob",
    });
    openPdfBlob(res.data, `lost_declaration_${rec.replaceAll("/", "-")}.pdf`);
  } catch (e) {
    try {
      const blob = e?.response?.data;
      const asText = blob && typeof blob.text === "function" ? await blob.text() : "";
      alert(asText || "Report failed. Please try again.");   // shows real server message
    } catch {
      alert("Report failed. Please try again.");
    }
  }
};

  // Enter key flow (same UX as Renew)
  const onMMYYKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      rRef.current?.focus();
      rRef.current?.select?.();
    }
  };
  const onReceiptKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <div className="renew-container">
      <h2 className="title">Report Lost Ticket</h2>

      {/* Search form */}
      <div className="form-group">
        <label>Enter Month and Year (MMYY):</label>
        <input
          ref={mRef}
          inputMode="numeric"
          maxLength={4}
          value={mmyy}
          onChange={(e) => setMMYY(cleanMMYY(e.target.value))}
          onKeyDown={onMMYYKeyDown}
          placeholder="MMYY"
        />
      </div>

      <div className="form-group">
        <label>Enter Receipt Last Digits:</label>
        <input
          ref={rRef}
          inputMode="numeric"
          maxLength={4}
          value={receiptNo}
          onChange={(e) => setReceiptNo(cleanDigits(e.target.value))}
          onKeyDown={onReceiptKeyDown}
          placeholder="receiptNo"
        />
      </div>

      <button className="search-btn" onClick={handleSearch}>Search</button>
      {error && <p className="error-message">{error}</p>}

      {/* Transaction & confirmation */}
      {transaction && (
        <div className="transaction-details">
          <div className="details-grid">
            <p><strong>Items:</strong> {transaction.item}</p>
            <p><strong>Weight:</strong> {transaction.weight}</p>
            <p><strong>Amount:</strong> ${Number(transaction.amount || 0).toFixed(2)}</p>
            <p><strong>Date:</strong> {fmtDate(transaction.grantDate)}</p>
            <p><strong>Expiry Date:</strong> {fmtDate(transaction.expiryDate)}</p>
          </div>

          {confirmStep === 0 && (
            <div className="alert-box">
              <p><strong>Are you sure you want to report this ticket as lost?</strong></p>
              <button onClick={() => setConfirmStep(1)}>Yes</button>
              <button onClick={() => setConfirmStep(0)}>No</button>
            </div>
          )}

          {confirmStep === 1 && (
            <div className="alert-box">
              <p><strong>Final confirmation: This action cannot be undone.</strong></p>
              <button onClick={handleReportLost}>Yes</button>
              <button onClick={() => setConfirmStep(0)}>No</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
